import { useState, useEffect, useRef } from "react";
import CompressorTrendFaceplate from "./CompressorTrendFaceplate";

import { 
  compressorSettings,
  startCompressor,
  stopCompressor,
  updateCompressor
} from "./SingleStageCompressorEngine";

import CompressorSVG from "./CompressorSVG";

import "./SingleStageCompressor.css";

function SingleStageCompressorFaceplate({ 
  input,
  close,
  openTrend,
  compressor,
}) {

  // ======================================================
  // FORCE UI REFRESH
  // ======================================================

  const [, forceUpdate] = useState(0);

  const refreshUI = () => forceUpdate(v => v + 1);


  // ======================================================
  // DRAGGING
  // ======================================================

  const faceplateRef = useRef(null);

  const [position, setPosition] = useState({
    x: 300,
    y: 120
  });

  const [dragging, setDragging] = useState(false);

  const dragOffset = useRef({
    x: 0,
    y: 0
  });

  // ======================================================
  // PERMISSIVES
  // ======================================================

  const [suctionTempOK, setSuctionTempOK] =
    useState("Yes");

  const [knockoutLevel, setKnockoutLevel] =
    useState("Normal");

  const [antiSurgeValve, setAntiSurgeValve] =
    useState("Open");

  const [noAlarms, setNoAlarms] =
    useState("No");

  const [lubeOilPressure, setLubeOilPressure] =
    useState("Okay");

  const [sealOilPressure, setSealOilPressure] =
    useState("Okay");

  // ======================================================
  // ENGINE LOOP
  // ======================================================

  useEffect(() => {

    const loop = setInterval(() => {

      updateCompressor(input);

      // ======================================================
      // CHECK PERMISSIVES FROM FACEPLATE VALUES
      // ======================================================

      const suctionPressureOK =
        compressor.PT_suction >=
        compressorSettings.minSuctionPressure;

      const suctionTempPass =
        suctionTempOK === "Yes";

      const knockoutPass =
        knockoutLevel === "Normal";

      const antiSurgePass =
        antiSurgeValve === "Open";

      const alarmsPass =
        noAlarms === "No";

      const lubeOilPass =
        lubeOilPressure === "Okay";

      const sealOilPass =
        sealOilPressure === "Okay";

      // ======================================================
      // FINAL PERMISSIVE
      // ======================================================

      const permissiveOK =

        suctionPressureOK &&
        suctionTempPass &&
        knockoutPass &&
        antiSurgePass &&
        alarmsPass &&
        lubeOilPass &&
        sealOilPass;

      // ======================================================
      // STORE INTO ENGINE STATE
      // ======================================================

      compressor.permissivePassed =
        permissiveOK;

      compressor.suctionTempAcceptable =
        suctionTempOK;

      compressor.knockoutLevel =
        knockoutLevel;

      compressor.antiSurgeValve =
        antiSurgeValve;

      compressor.noAlarms =
        noAlarms;

      compressor.lubeOilPressure =
        lubeOilPressure;

      compressor.sealOilPressure =
        sealOilPressure;

      // ======================================================
      // NOT READY IF FAILED
      // ======================================================

      if (
        !permissiveOK &&
        !compressor.running &&
        !compressor.starting
      ) {

        compressor.ready = false;

        compressor.notReady = true;

        compressor.status =
          "NOT READY";

        // ======================================================
        // FAILURE MESSAGE
        // ======================================================

        if (!suctionPressureOK) {

          compressor.sequenceMessage =
            "LOW SUCTION PRESSURE";
        }

        else if (!suctionTempPass) {

          compressor.sequenceMessage =
            "SUCTION TEMPERATURE NOT ACCEPTABLE";
        }

        else if (!knockoutPass) {

          compressor.sequenceMessage =
            "KNOCKOUT DRUM LEVEL ABNORMAL";
        }

        else if (!antiSurgePass) {

          compressor.sequenceMessage =
            "ANTI-SURGE VALVE CLOSED";
        }

        else if (!alarmsPass) {

          compressor.sequenceMessage =
            "ACTIVE ALARMS PRESENT";
        }

        else if (!lubeOilPass) {

          compressor.sequenceMessage =
            "LUBE OIL PRESSURE NOT OKAY";
        }

        else if (!sealOilPass) {

          compressor.sequenceMessage =
            "SEAL OIL PRESSURE NOT OKAY";
        }
      }

      // ======================================================
      // READY STATE
      // ======================================================

      else if (
        permissiveOK &&
        !compressor.running &&
        !compressor.starting &&
        !compressor.shuttingDown
      ) {

        compressor.ready = true;

        compressor.notReady = false;

        compressor.status = "READY";

        compressor.sequenceMessage =
          "ALL PERMISSIVES SATISFIED";
      }

      refreshUI();

    }, 100);

    return () => clearInterval(loop);

  }, [

    input,

    suctionTempOK,

    knockoutLevel,

    antiSurgeValve,

    noAlarms,

    lubeOilPressure,

    sealOilPressure
  ]);

  // ======================================================
  // DRAG START
  // ======================================================

  const handleMouseDown = (e) => {

    setDragging(true);

    dragOffset.current = {

      x: e.clientX - position.x,

      y: e.clientY - position.y
    };
  };

  // ======================================================
  // DRAGGING
  // ======================================================

  useEffect(() => {

    const handleMouseMove = (e) => {

      if (!dragging) return;

      setPosition({

        x: e.clientX - dragOffset.current.x,

        y: e.clientY - dragOffset.current.y
      });
    };

    const handleMouseUp = () => {

      setDragging(false);
    };

    window.addEventListener(
      "mousemove",
      handleMouseMove
    );

    window.addEventListener(
      "mouseup",
      handleMouseUp
    );

    return () => {

      window.removeEventListener(
        "mousemove",
        handleMouseMove
      );

      window.removeEventListener(
        "mouseup",
        handleMouseUp
      );
    };

  }, [dragging]);

  // ======================================================
  // STATUS
  // ======================================================

  const compressorStatus =
    compressor.status;





  // ======================================================
  // UI
  // ======================================================

  return (

    <div
      ref={faceplateRef}
      className="compressor-faceplate"

      style={{
        left: position.x,
        top: position.y,
        position: "absolute",
        zIndex: 99999
      }}
    >

      {/* ======================================================
          HEADER
      ====================================================== */}

      <div
        className="compressor-header"
        onMouseDown={handleMouseDown}
        >

        <div className="compressor-title">
          NK TECH COMPRESSORS
        </div>

        <div className="compressor-header-right">

          <button
            className="close-btn"
            onClick={(e) => {
                e.stopPropagation();   // VERY IMPORTANT
                e.preventDefault();
                close?.();
            }}
            >
            ✕
            </button>

        </div>

      </div>

      {/* ======================================================
          BODY
      ====================================================== */}

      <div className="compressor-body">

        {/* ======================================================
            LEFT PANEL
        ====================================================== */}

        <div className="compressor-left">

          {/* ================= SUCTION ================= */}

          <div className="section-title">
            SUCTION CONDITIONS
          </div>

          <div className="live-row">
            <span>Pressure</span>

            <span>
              {compressor.PT_suction.toFixed(2)} MPa
            </span>
          </div>

          <div className="live-row">
            <span>Temperature</span>

            <span>
              {compressor.TT_suction.toFixed(2)} °C
            </span>
          </div>

          <div className="live-row">
            <span>Flowrate</span>

            <span>
              {compressor.FIC_suction.toFixed(2)}
            </span>
          </div>

          {/* ================= DISCHARGE ================= */}

          <div className="section-title">
            DISCHARGE CONDITIONS
          </div>

          <div className="live-row">
            <span>Pressure</span>

            <span>
              {compressor.PT_discharge.toFixed(2)} MPa
            </span>
          </div>

          <div className="live-row">
            <span>Temperature</span>

            <span>
              {compressor.TT_discharge.toFixed(2)} °C
            </span>
          </div>

          <div className="live-row">
            <span>Flowrate</span>

            <span>
              {compressor.FIC_discharge.toFixed(2)}
            </span>
          </div>

          {/* ================= OPERATING LIMITS ================= */}

          <div className="section-title">
            OPERATING LIMITS
          </div>

          <div className="setting-row">

            <label>
              Minimum Suction Pressure
            </label>

            <input
              type="number"

              value={
                compressorSettings.minSuctionPressure
              }

              onChange={(e) =>
                compressorSettings.minSuctionPressure =
                  Number(e.target.value)
              }
            />

          </div>

          <div className="setting-row">

            <label>
              Low Alarm
            </label>

            <input
              type="number"

              value={
                compressorSettings.lowAlarm
              }

              onChange={(e) =>
                compressorSettings.lowAlarm =
                  Number(e.target.value)
              }
            />

          </div>

          <div className="setting-row">

            <label>
              Low Low Trip
            </label>

            <input
              type="number"

              value={
                compressorSettings.lowLowTrip
              }

              onChange={(e) =>
                compressorSettings.lowLowTrip =
                  Number(e.target.value)
              }
            />

          </div>

          <div className="setting-row">

            <label>
              High Alarm
            </label>

            <input
              type="number"

              value={
                compressorSettings.highAlarm
              }

              onChange={(e) =>
                compressorSettings.highAlarm =
                  Number(e.target.value)
              }
            />

          </div>

          {/* ================= START PERMISSIVES ================= */}

          <div className="section-title">
            START PERMISSIVES
          </div>

          <div className="live-row">

            <span>
              Suction Pressure Above Minimum
            </span>

            <span>
              {
                compressor.PT_suction >=
                compressorSettings.minSuctionPressure
                  ? "YES"
                  : "NO"
              }
            </span>

          </div>

          <div className="setting-row">

            <label>
              Suction Temperature Acceptable
            </label>

            <select
              value={suctionTempOK}

              onChange={(e) =>
                setSuctionTempOK(
                  e.target.value
                )
              }
            >
              <option>Yes</option>
              <option>No</option>
            </select>

          </div>

          <div className="setting-row">

            <label>
              Knockout Drum Liquid Level
            </label>

            <select
              value={knockoutLevel}

              onChange={(e) =>
                setKnockoutLevel(
                  e.target.value
                )
              }
            >
              <option>HH</option>
              <option>H</option>
              <option>Normal</option>
              <option>L</option>
              <option>LL</option>
            </select>

          </div>

          <div className="setting-row">

            <label>
              Anti-Surge Valve
            </label>

            <select
              value={antiSurgeValve}

              onChange={(e) =>
                setAntiSurgeValve(
                  e.target.value
                )
              }
            >
              <option>Open</option>
              <option>Closed</option>
            </select>

          </div>

          <div className="setting-row">

            <label>
              No Alarms
            </label>

            <select
              value={noAlarms}

              onChange={(e) =>
                setNoAlarms(
                  e.target.value
                )
              }
            >
              <option>No</option>
              <option>Yes</option>
            </select>

          </div>

          <div className="setting-row">

            <label>
              Lube Oil Pressure
            </label>

            <select
              value={lubeOilPressure}

              onChange={(e) =>
                setLubeOilPressure(
                  e.target.value
                )
              }
            >
              <option>Okay</option>
              <option>High</option>
              <option>Low</option>
            </select>

          </div>

          <div className="setting-row">

            <label>
              Seal Oil Pressure
            </label>

            <select
              value={sealOilPressure}

              onChange={(e) =>
                setSealOilPressure(
                  e.target.value
                )
              }
            >
              <option>Okay</option>
              <option>High</option>
              <option>Low</option>
            </select>

          </div>

        </div>

        {/* ======================================================
            RIGHT PANEL
        ====================================================== */}

        <div className="compressor-right">

          <div
                className="blinking-text"
                style={{
                    width: "80%",
                    padding: "10px",
                    marginBottom: "2px",
                    border: "1px solid #00d2ff",
                    borderRadius: "6px",
                    background: "#081018",
                    color: "#00ffae",
                    fontSize: "18px",
                    textAlign: "center",
                    minHeight: "20px"
                }}
                >
                {compressor.sequenceMessage ?? compressor.status}
                </div>

          <CompressorSVG
            running={compressor.running}
            tripped={compressor.tripped}
          />

          <div className="compressor-diagnostics">

            <div className="diag-box">

              <div className="diag-title">
                MACHINE STATUS
              </div>

              <div className="diag-value">
                {compressorStatus}
              </div>

            </div>

            <div className="diag-box">

              <div className="diag-title">
                SPEED
              </div>

              <div className="diag-value">
                {
                  compressor.speed.toFixed(0)
                } RPM
              </div>

            </div>

            <div className="diag-box">

              <div className="diag-title">
                EFFICIENCY
              </div>

              <div className="diag-value">
                82%
              </div>

            </div>

          </div>

        </div>

      </div>

      {/* ======================================================
          FOOTER
      ====================================================== */}

      <div className="compressor-footer">

        <button
          className={
            compressor.running ||
            compressor.starting ||
            compressor.startSequenceActive
              ? "start-btn active"
              : "start-btn"
          }

          onClick={() => {
              startCompressor(compressor);
              refreshUI();
            }}
        >
          START
        </button>

        <button
          className={
            !compressor.running &&
            !compressor.starting
              ? "stop-btn active"
              : "stop-btn"
          }

          onClick={() => {
              stopCompressor(compressor);
              refreshUI();
            }}
        >
          STOP
        </button>

        <button
          className="reset-btn"

          onClick={() => {
            compressor.tripped = false;
            compressor.running = false;
            compressor.starting = false;
            compressor.shuttingDown = false;
            compressor.status = "STOPPED";
            compressor.sequenceMessage = "SYSTEM RESET";

            refreshUI();
          }}
        >
          RESET
        </button>

        <button
            className="graph-btn"
            onClick={(e) => {
                e.stopPropagation();
                openTrend?.();   // 👈 opens external overlay
            }}
            >
            📈 GRAPH
            </button>

      </div>

      

    </div>
  );
}

export default SingleStageCompressorFaceplate;